import moment from 'moment';
import React, {ChangeEvent, FC} from 'react';
import {Alert} from '@material-ui/lab';
import Box from '@material-ui/core/Box';
import {withStyles} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import {getLocale} from 'epic-common/src/locale';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog, {DialogProps} from '@material-ui/core/Dialog';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import {timeRangeWarningThresholds} from './constants';

import {DateMoment, TimeScale} from '../../../../../../types';

const CODES = {
    title: 'epic.affiliate.fortnite_games.limit_warning.title',
    confirm: 'epic.affiliate.fortnite_games.limit_warning.confirm',
    cancel: 'epic.affiliate.common.cancel',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    description: 'epic.affiliate.fortnite_games.time_range_limit.description',
    threshold: 'epic.affiliate.fortnite_games.time_range_limit.threshold',
    warning: 'epic.affiliate.fortnite_games.time_range_limit.warning',
    doNotShow: 'epic.affiliate.fortnite_games.time_range_limit.do_not_show'
};

export const StyledDialog = withStyles(theme => ({
    paper: {
        maxWidth: '480px',

        [theme.breakpoints.up('xl')]: {
            maxWidth: '540px'
        }
    }
}))(Dialog);

const StyledContent = withStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',

        '&> h2, & > p:last-of-type, & > div:first-of-type': {
            marginBottom: theme.spacing(8)
        },

        '&> div:nth-of-type(2), & > p:first-of-type': {
            marginBottom: theme.spacing(4)
        },

        '& > b': {
            textTransform: 'capitalize'
        }
    }
}))(DialogContent);

export interface WarningModalProps extends DialogProps {
    startDate?: DateMoment;
    endDate?: DateMoment;
    timeScale?: TimeScale;
    onConfirm: () => void;
    onCancel: () => void;
    doNotShowAgain: boolean;
    onDoNotShowChange: (event: ChangeEvent<HTMLInputElement>) => void;
    getMessage: (code: string, args?: any[]) => string;
}

const TimeRangeWarningModal: FC<WarningModalProps> = ({
    startDate,
    endDate,
    timeScale,
    doNotShowAgain,
    onConfirm,
    onCancel,
    onDoNotShowChange,
    getMessage,
    ...props
}: WarningModalProps) => {
    const [input, diff] = timeRangeWarningThresholds[timeScale] || [];

    const formatter = new Intl.DateTimeFormat(getLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const range = startDate
        ? `${formatter.format(startDate.valueOf())} - ${formatter.format(
              endDate.valueOf()
          )} ${getMessage(CODES.threshold, [moment.duration(input as number, diff).humanize()])}`
        : '';

    return (
        <StyledDialog {...props}>
            <StyledContent>
                <Typography variant="h2" color="textPrimary" align="center">
                    {getMessage(CODES.title)}
                </Typography>
                <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    dangerouslySetInnerHTML={{
                        /* Since there is no "react-i18next" package with "Trans" component in the project
                         * we have to use "dangerouslySetInnerHTML" for inline html in translations
                         *  */
                        __html: getMessage(CODES.description, [
                            getMessage(`${CODES.timeScale}${timeScale}`)
                        ])
                    }}
                />
                <Typography variant="body1" color="textPrimary" align="center">
                    • {range}
                </Typography>
                <Alert severity="warning" variant="standard">
                    {getMessage(CODES.warning)}
                </Alert>
                <Button size="large" color="primary" component="div" onClick={onConfirm}>
                    {getMessage(CODES.confirm)}
                </Button>
                <Button size="large" color="secondary" component="div" onClick={onCancel}>
                    {getMessage(CODES.cancel)}
                </Button>
                <Box display="flex" alignItems="center" pt={4} clone>
                    <Typography
                        component="label"
                        variant="body1"
                        color="textSecondary"
                        align="center">
                        <Checkbox onChange={onDoNotShowChange} checked={doNotShowAgain} />
                        {getMessage(CODES.doNotShow)}
                    </Typography>
                </Box>
            </StyledContent>
        </StyledDialog>
    );
};

export default withLocalization(TimeRangeWarningModal);
